import { Controller } from "stimulus"

// #1856: de universele zoekfunctie (layouts/_zoekvenster).
//
// Opent met alles wat data-zoeken-open heeft (het zoekveld bovenaan, het
// zoekicoon op de telefoon) en met Ctrl+K / Cmd+K. Tijdens het typen haalt hij
// /zoeken/snel op; die lijst is op de server gemaakt, met de rechten in de
// query. Pijltjes kiezen een resultaat, Enter opent het; Enter zonder keuze
// opent de volledige resultatenpagina. Recente zoekopdrachten staan per
// gebruiker in localStorage (zonder opslag, zoals in een privévenster, werkt
// alles behalve die lijst).
const WACHT = 150
const MIN_LENGTE = 2
const MAX_RECENT = 6

export default class extends Controller {
  static targets = ["veld", "lijst", "optie"]
  static values = { snel: String, opslag: String, recent: String, wissen: String, hint: String, mac: String }

  connect() {
    this.actief = -1
    this.teller = 0
    this.toets = this.toets.bind(this)
    this.sneltoets = this.sneltoets.bind(this)
    this.openKlik = this.openKlik.bind(this)
    this.voorCache = () => this.sluit({ focus: false })
    document.addEventListener("keydown", this.sneltoets)
    document.addEventListener("click", this.openKlik)
    document.addEventListener("turbo:before-cache", this.voorCache)
    this.toonSneltoets()
  }

  disconnect() {
    document.removeEventListener("keydown", this.sneltoets)
    document.removeEventListener("click", this.openKlik)
    document.removeEventListener("turbo:before-cache", this.voorCache)
    clearTimeout(this.timer)
    if (this.verzoek) this.verzoek.abort()
  }

  get open() {
    return !this.element.hidden
  }

  // Op een Mac staat er ⌘ K bij het zoekveld in plaats van Ctrl K
  toonSneltoets() {
    const mac = /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent || "")
    if (!mac || !this.macValue) return
    document.querySelectorAll("[data-zoeken-sneltoets]").forEach((el) => { el.textContent = this.macValue })
  }

  sneltoets(event) {
    if ((event.metaKey || event.ctrlKey) && !event.altKey && !event.shiftKey && (event.key || "").toLowerCase() === "k") {
      event.preventDefault()
      if (this.open) {
        this.sluit()
      } else {
        this.terugFocus = document.activeElement
        this.openVenster()
      }
    } else if (event.key === "Escape" && this.open) {
      event.preventDefault()
      this.sluit()
    }
  }

  openKlik(event) {
    const knop = event.target.closest("[data-zoeken-open]")
    if (!knop || event.defaultPrevented || event.button !== 0) return
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

    event.preventDefault()
    this.terugFocus = knop
    this.openVenster()
  }

  openVenster() {
    this.element.hidden = false
    this.veldTarget.focus()
    this.veldTarget.select()
    if (this.veldTarget.value.trim().length < MIN_LENGTE) this.toonRecent()
  }

  sluit({ focus = true } = {}) {
    if (!this.open) return
    this.element.hidden = true
    this.veldTarget.setAttribute("aria-expanded", "false")
    if (focus && this.terugFocus && document.contains(this.terugFocus)) this.terugFocus.focus()
  }

  typ() {
    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.haal(), WACHT)
  }

  async haal() {
    const q = this.veldTarget.value.trim()
    if (q.length < MIN_LENGTE) {
      this.toonRecent()
      return
    }
    if (this.verzoek) this.verzoek.abort()
    this.verzoek = new AbortController()
    const nummer = ++this.teller
    const url = new URL(this.snelValue, window.location.origin)
    url.searchParams.set("q", q)
    try {
      const antwoord = await fetch(url, {
        headers: { Accept: "text/html", "X-Requested-With": "XMLHttpRequest" },
        credentials: "same-origin",
        signal: this.verzoek.signal
      })
      if (!antwoord.ok || nummer !== this.teller) return
      const html = await antwoord.text()
      if (nummer !== this.teller) return
      // De lijst komt van onze eigen server en is daar al ge-escaped
      this.lijstTarget.innerHTML = html
      this.kies(-1)
      this.veldTarget.setAttribute("aria-expanded", "true")
    } catch (fout) {
      if (fout.name !== "AbortError") this.toonHint()
    }
  }

  toets(event) {
    const opties = this.optieTargets
    if (event.key === "ArrowDown") {
      event.preventDefault()
      if (opties.length) this.kies(this.actief >= opties.length - 1 ? 0 : this.actief + 1)
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      if (opties.length) this.kies(this.actief <= 0 ? opties.length - 1 : this.actief - 1)
    } else if (event.key === "Enter") {
      const optie = opties[this.actief]
      if (!optie) return
      event.preventDefault()
      this.kiesOptie(optie)
    }
  }

  kies(index) {
    this.actief = index
    this.optieTargets.forEach((optie, i) => {
      const aan = i === index
      optie.classList.toggle("is-actief", aan)
      optie.setAttribute("aria-selected", aan ? "true" : "false")
      if (aan) {
        this.veldTarget.setAttribute("aria-activedescendant", optie.id)
        optie.scrollIntoView({ block: "nearest" })
      }
    })
    if (index < 0) this.veldTarget.removeAttribute("aria-activedescendant")
  }

  kiesOptie(optie) {
    if (optie.dataset.zoekRecent !== undefined) {
      this.veldTarget.value = optie.dataset.zoekRecent
      this.veldTarget.focus()
      this.haal()
      return
    }
    this.bewaarRecent(this.veldTarget.value)
    const href = optie.getAttribute("href")
    this.sluit({ focus: false })
    if (window.Turbo && typeof window.Turbo.visit === "function") {
      window.Turbo.visit(href)
    } else {
      window.location.href = href
    }
  }

  // Een klik in de lijst: een resultaat opent gewoon (de link), een recente
  // zoekopdracht vult het veld
  gekozen(event) {
    const wis = event.target.closest("[data-zoek-recent-wissen]")
    if (wis) {
      event.preventDefault()
      this.schrijfRecent([])
      this.toonRecent()
      this.veldTarget.focus()
      return
    }
    const optie = event.target.closest("[role='option']")
    if (!optie) return
    if (optie.dataset.zoekRecent !== undefined) {
      event.preventDefault()
      this.kiesOptie(optie)
    } else {
      this.bewaarRecent(this.veldTarget.value)
      this.sluit({ focus: false })
    }
  }

  verstuur(event) {
    const q = this.veldTarget.value.trim()
    if (q.length < MIN_LENGTE) {
      event.preventDefault()
      return
    }
    this.bewaarRecent(q)
    this.sluit({ focus: false })
  }

  // ---------- recente zoekopdrachten ----------

  leesRecent() {
    try {
      const lijst = JSON.parse(window.localStorage.getItem(this.opslagValue) || "[]")
      return Array.isArray(lijst) ? lijst.filter((q) => typeof q === "string").slice(0, MAX_RECENT) : []
    } catch (e) {
      return []
    }
  }

  schrijfRecent(lijst) {
    try {
      window.localStorage.setItem(this.opslagValue, JSON.stringify(lijst.slice(0, MAX_RECENT)))
    } catch (e) {
      // Geen opslag beschikbaar: dan onthouden we niets
    }
  }

  bewaarRecent(q) {
    const tekst = (q || "").trim()
    if (tekst.length < MIN_LENGTE) return
    const lijst = this.leesRecent().filter((oud) => oud.toLowerCase() !== tekst.toLowerCase())
    this.schrijfRecent([tekst, ...lijst])
  }

  toonHint() {
    const p = document.createElement("p")
    p.className = "cc-zoek-leeg"
    p.textContent = this.hintValue
    this.lijstTarget.replaceChildren(p)
    this.kies(-1)
  }

  toonRecent() {
    const recent = this.leesRecent()
    if (!recent.length) {
      this.toonHint()
      return
    }
    const groep = document.createElement("div")
    groep.className = "cc-zoek-groep"
    groep.setAttribute("role", "group")
    groep.setAttribute("aria-labelledby", "cc-zoek-recent-kop")
    groep.dataset.zoekGroep = "recent"

    const kop = document.createElement("div")
    kop.className = "cc-zoek-groep-kop cc-zoek-recent-kop"
    const titel = document.createElement("span")
    titel.id = "cc-zoek-recent-kop"
    titel.textContent = this.recentValue
    const wis = document.createElement("button")
    wis.type = "button"
    wis.className = "cc-zoek-recent-wis"
    wis.dataset.zoekRecentWissen = ""
    wis.textContent = this.wissenValue
    kop.append(titel, wis)
    groep.appendChild(kop)

    recent.forEach((q, index) => {
      const knop = document.createElement("button")
      knop.type = "button"
      knop.className = "cc-zoek-recent-item"
      knop.id = `cc-zoek-recent-${index}`
      knop.setAttribute("role", "option")
      knop.setAttribute("aria-selected", "false")
      knop.dataset.zoekenTarget = "optie"
      knop.dataset.zoekRecent = q
      const tekst = document.createElement("span")
      tekst.className = "cc-zoek-titel"
      tekst.textContent = q
      knop.appendChild(tekst)
      groep.appendChild(knop)
    })
    this.lijstTarget.replaceChildren(groep)
    this.kies(-1)
  }
}
